import { AvailabilityStatus } from '@wix/ambassador-restaurants-menu-settings-v1-menu-ordering-settings/types';
import type { MenuAvailability } from '@wix/restaurants-olo-operations-client-commons';

import type {
  MenuSectionAvailability,
  PopulatedMenu,
  PopulatedMenuSection,
  MenuItem,
} from '../types/menu';
import type {
  MenuAvailabilityStatusById,
  SectionAvailabilityStatusById,
} from '../types/menuAvailability';

import {
  getItemAvailabilityPlacementId,
  hasFutureAvailability,
} from './menuAvailability';
import type { ItemAvailabilityByPlacementId } from './menuAvailability';

export type MenuOrderByLocation = Record<string, string[]>;

export interface LocationMenu {
  id: string;
  name: string;
}

export const parseMenuOrder = (json: string): MenuOrderByLocation => {
  if (!json) {
    return {};
  }
  try {
    return JSON.parse(json);
  } catch {
    return {};
  }
};

/**
 * Sorts menus according to a previously stored order.
 * Menus that exist in storedOrder keep their saved position;
 * menus not found in storedOrder (newly added) are appended at the end.
 */
type OrderableMenu = LocationMenu | PopulatedMenu;

const isSameOrder = <T>(a: T[], b: T[]): boolean =>
  a.length === b.length && a.every((item, index) => item === b[index]);

const getPlacementItemAvailability = (
  item: MenuItem,
  itemAvailabilityByPlacementId: ItemAvailabilityByPlacementId,
  placement: { menuId: string; sectionId: string },
) => {
  const placementId = getItemAvailabilityPlacementId(
    placement.menuId,
    placement.sectionId,
    item.id ?? '',
  );

  return itemAvailabilityByPlacementId[placementId];
};

export const getOrderedMenus = <T extends OrderableMenu>(
  menus: T[],
  storedOrder?: string[],
): T[] => {
  if (!storedOrder?.length) {
    return menus;
  }

  // Resolve stored IDs to menu objects, dropping any that no longer exist
  const result = storedOrder
    .map((id) => menus.find((m) => m.id === id))
    .filter((m): m is T => !!m);

  // Append any menus not present in stored order (e.g. newly created)
  menus.forEach((m) => {
    if (!result.includes(m)) {
      result.push(m);
    }
  });

  return result;
};

const isItemUnavailable = (
  item: MenuItem,
  itemAvailabilityByPlacementId: ItemAvailabilityByPlacementId = {},
  placement: { menuId: string; sectionId: string },
): boolean =>
  getPlacementItemAvailability(item, itemAvailabilityByPlacementId, placement)
    ?.isAvailable === false || item.orderSettings?.inStock === false;

const isMenuAvailable = (
  menu: PopulatedMenu,
  menuAvailabilityStatusById: MenuAvailabilityStatusById = {},
): boolean =>
  menuAvailabilityStatusById[menu.id] !== AvailabilityStatus.UNAVAILABLE;

const hasNextAvailability = (
  menu: PopulatedMenu,
  menuNextAvailabilityById: Record<string, boolean> = {},
): boolean => !!menuNextAvailabilityById[menu.id];

const isSectionAvailable = (
  section: PopulatedMenuSection,
  sectionAvailabilityStatusById: SectionAvailabilityStatusById = {},
): boolean =>
  sectionAvailabilityStatusById[section.id] !== AvailabilityStatus.UNAVAILABLE;

const getSectionAvailability = (
  section: PopulatedMenuSection,
  sectionAvailabilityById: Partial<
    Record<string, MenuSectionAvailability>
  > = {},
): MenuSectionAvailability | undefined =>
  sectionAvailabilityById[section.id] ?? section.orderSettings?.availability;

const applyMenuAvailabilitySettings = (
  menus: PopulatedMenu[],
  options: {
    showUnavailableOptions: boolean;
    showExpired: boolean;
    showUnavailableAndExpiredLast: boolean;
    menuAvailabilityStatusById?: MenuAvailabilityStatusById;
    menuNextAvailabilityById?: Record<string, boolean>;
    menuAvailabilityById?: Record<string, MenuAvailability>;
    timezone?: string;
  },
): PopulatedMenu[] => {
  const filteredMenus = menus.filter((menu) => {
    if (isMenuAvailable(menu, options.menuAvailabilityStatusById)) {
      return true;
    }

    if (!options.showUnavailableOptions) {
      return false;
    }

    return (
      options.showExpired ||
      hasNextAvailability(menu, options.menuNextAvailabilityById) ||
      hasFutureAvailability(
        options.menuAvailabilityById?.[menu.id],
        options.timezone,
      )
    );
  });

  if (!options.showUnavailableAndExpiredLast) {
    return filteredMenus;
  }

  return [...filteredMenus].sort((a, b) => {
    const aAvailable = isMenuAvailable(a, options.menuAvailabilityStatusById);
    const bAvailable = isMenuAvailable(b, options.menuAvailabilityStatusById);

    if (aAvailable !== bAvailable) {
      return aAvailable ? -1 : 1;
    }

    const aHasNextAvailability = hasNextAvailability(
      a,
      options.menuNextAvailabilityById,
    );
    const bHasNextAvailability = hasNextAvailability(
      b,
      options.menuNextAvailabilityById,
    );

    if (aHasNextAvailability !== bHasNextAvailability) {
      return aHasNextAvailability ? -1 : 1;
    }

    return 0;
  });
};

const filterOutUnavailable = (
  menuId: string,
  sectionId: string,
  itemIds: string[],
  itemMap: Record<string, MenuItem>,
  itemAvailabilityByPlacementId?: ItemAvailabilityByPlacementId,
): string[] => {
  const filteredItemIds = itemIds.filter((id) => {
    const item = itemMap[id];

    return (
      !item ||
      !isItemUnavailable(item, itemAvailabilityByPlacementId, {
        menuId,
        sectionId,
      })
    );
  });

  return filteredItemIds.length === itemIds.length ? itemIds : filteredItemIds;
};

const sortUnavailableLast = (
  menuId: string,
  sectionId: string,
  itemIds: string[],
  itemMap: Record<string, MenuItem>,
  itemAvailabilityByPlacementId?: ItemAvailabilityByPlacementId,
): string[] => {
  const sortedItemIds = [...itemIds].sort((a, b) => {
    const aUnavailable = itemMap[a]
      ? isItemUnavailable(itemMap[a], itemAvailabilityByPlacementId, {
          menuId,
          sectionId,
        })
      : false;
    const bUnavailable = itemMap[b]
      ? isItemUnavailable(itemMap[b], itemAvailabilityByPlacementId, {
          menuId,
          sectionId,
        })
      : false;

    if (aUnavailable === bUnavailable) {
      return 0;
    }

    return aUnavailable ? 1 : -1;
  });

  return isSameOrder(sortedItemIds, itemIds) ? itemIds : sortedItemIds;
};

const applyItemAvailabilitySettings = (
  menuId: string,
  sectionId: string,
  itemIds: string[],
  itemMap: Record<string, MenuItem>,
  options: {
    showUnavailableOptions: boolean;
    showUnavailableAndExpiredLast: boolean;
    itemAvailabilityByPlacementId?: ItemAvailabilityByPlacementId;
  },
): string[] => {
  if (!options.showUnavailableOptions) {
    return filterOutUnavailable(
      menuId,
      sectionId,
      itemIds,
      itemMap,
      options.itemAvailabilityByPlacementId,
    );
  }

  if (options.showUnavailableAndExpiredLast) {
    return sortUnavailableLast(
      menuId,
      sectionId,
      itemIds,
      itemMap,
      options.itemAvailabilityByPlacementId,
    );
  }

  return itemIds;
};

const applySectionItemAvailability = (
  menuId: string,
  sections: PopulatedMenu['sections'],
  itemMap: Record<string, MenuItem>,
  options: {
    showUnavailableOptions: boolean;
    showUnavailableAndExpiredLast: boolean;
    itemAvailabilityByPlacementId?: ItemAvailabilityByPlacementId;
  },
): PopulatedMenu['sections'] => {
  let hasChanged = false;
  const nextSections = sections.map((section) => {
    const itemIds = applyItemAvailabilitySettings(
      menuId,
      section.id,
      section.itemIds,
      itemMap,
      options,
    );

    if (itemIds === section.itemIds) {
      return section;
    }

    hasChanged = true;

    return { ...section, itemIds, size: itemIds.length };
  });

  return hasChanged ? nextSections : sections;
};

const applySectionAvailabilitySettings = (
  sections: PopulatedMenu['sections'],
  options: {
    showUnavailableOptions: boolean;
    showExpired: boolean;
    showUnavailableAndExpiredLast: boolean;
    sectionAvailabilityStatusById?: SectionAvailabilityStatusById;
    sectionAvailabilityById?: Partial<Record<string, MenuSectionAvailability>>;
    timezone?: string;
  },
): PopulatedMenu['sections'] => {
  const filteredSections = sections.filter((section) => {
    if (isSectionAvailable(section, options.sectionAvailabilityStatusById)) {
      return true;
    }

    if (!options.showUnavailableOptions) {
      return false;
    }

    return (
      options.showExpired ||
      hasFutureAvailability(
        getSectionAvailability(section, options.sectionAvailabilityById),
        options.timezone,
      )
    );
  });

  const visibleSections =
    filteredSections.length === sections.length ? sections : filteredSections;

  if (!options.showUnavailableAndExpiredLast) {
    return visibleSections;
  }

  const sortedSections = [...visibleSections].sort((a, b) => {
    const aAvailable = isSectionAvailable(
      a,
      options.sectionAvailabilityStatusById,
    );
    const bAvailable = isSectionAvailable(
      b,
      options.sectionAvailabilityStatusById,
    );

    if (aAvailable !== bAvailable) {
      return aAvailable ? -1 : 1;
    }

    if (aAvailable && bAvailable) {
      return 0;
    }

    const aHasFutureAvailability = hasFutureAvailability(
      getSectionAvailability(a, options.sectionAvailabilityById),
      options.timezone,
    );
    const bHasFutureAvailability = hasFutureAvailability(
      getSectionAvailability(b, options.sectionAvailabilityById),
      options.timezone,
    );

    if (aHasFutureAvailability !== bHasFutureAvailability) {
      return aHasFutureAvailability ? -1 : 1;
    }

    return 0;
  });

  return isSameOrder(sortedSections, visibleSections)
    ? visibleSections
    : sortedSections;
};

export const applyMenuEditorSettings = (
  menus: PopulatedMenu[],
  itemMap: Record<string, MenuItem>,
  options: {
    showUnavailableOptions: boolean;
    showExpired: boolean;
    showUnavailableAndExpiredLast: boolean;
    menuAvailabilityStatusById?: MenuAvailabilityStatusById;
    menuNextAvailabilityById?: Record<string, boolean>;
    menuAvailabilityById?: Record<string, MenuAvailability>;
    itemAvailabilityByPlacementId?: ItemAvailabilityByPlacementId;
    sectionAvailabilityStatusById?: SectionAvailabilityStatusById;
    sectionAvailabilityById?: Partial<Record<string, MenuSectionAvailability>>;
    timezone?: string;
  },
): PopulatedMenu[] => {
  return applyMenuAvailabilitySettings(menus, options).map((menu) => {
    const availabilitySections = applySectionAvailabilitySettings(
      menu.sections,
      options,
    );
    const sections = applySectionItemAvailability(
      menu.id,
      availabilitySections,
      itemMap,
      options,
    );
    const size = sections.reduce((sum, s) => sum + s.itemIds.length, 0);

    if (sections === menu.sections && size === menu.size) {
      return menu;
    }

    return { ...menu, sections, size };
  });
};
